"use client"

import { CONTACT_FORMS } from "@/constants/admin-paths"
import { useGuardedForm } from "@/hook/useGuardedForm"
import getDomains from "@/services/domain-services"
import { ContactFormFieldType } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import { useContactForms } from "./useContactForms"

export const useUpsertContactForm = () => {
  const [form] = Form.useForm()
  const router = useRouter()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = !!id

  const [initialValues, setInitialValues] = useState<ContactFormFieldType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [domainOptions, setDomainOptions] = useState<{ label: string; value: string }[]>([])
  const [status, setStatus] = useState<string>("Draft")
  const [isFormReady, setIsFormReady] = useState(false);
  const { loading, actions } = useContactForms()
  const { fetchContactFormByIdAndSetForm, handleUpsertContactForm } = actions
  const getNormalizedSnapshot = useCallback(() => {
    return {
      ...normalizeFormData(form.getFieldsValue(true)),
      status,
    };
  }, [form, status])


  const { isChange, checkChange, message, commitSnapshot, handleCancel: guardCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot
  });


  useEffect(() => {
    checkChange();
  }, [status, checkChange]);

  const fetchDomainOptions = useCallback(async () => {
    try {
      const response = await getDomains()
      if (response && Array.isArray(response)) {
        const options = response.map(domain => ({
          label: domain.attributes?.name || domain.attributes?.domain || "",
          value: domain.attributes?.domain || "",
        }))
        setDomainOptions(options)
      }
    } catch (error) {
      console.error("Failed to fetch domains for select", error)
    }
  }, [])

  const loadData = useCallback(async () => {
    if (isEditMode && id) {
      const { formData, contactForm } = await fetchContactFormByIdAndSetForm(Number(id), form)
      if (formData) {
        setStatus(contactForm?.attributes?.publishedAt ? "Published" : "Draft")
        setIsFormReady(true);
      }
    }
  }, [id, isEditMode, fetchContactFormByIdAndSetForm, form])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  useEffect(() => {
    fetchDomainOptions()
    if (isEditMode) loadData()
    else {
      setIsFormReady(true);
    }
  }, [loadData, isEditMode, fetchDomainOptions])

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()
      const result = await handleUpsertContactForm(values, id ? Number(id) : undefined, status)
      if (result.success) {
        commitSnapshot();
        if (!isEditMode) {
          const targetPath = createPath(CONTACT_FORMS.LIST.PATH, searchParams)
          router.push(targetPath)
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    const targetPath = createPath(CONTACT_FORMS.LIST.PATH, searchParams)
    guardCancel(targetPath)
  }

  return {
    form,
    isEditMode,
    loading,
    submitting,
    domainOptions,
    status,
    setStatus,
    isButtonDisabled: isEditMode ? !isChange : false,
    handlers: {
      handleSubmit,
      handleBack,
      handleFormChange: checkChange
    },
  }
}